import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';

import { postUserProductMutationFn } from '@/queries/products';
import { trackMP } from '@/shared/mp';

import { type ProductSelection, useCheckoutInterception } from './CheckoutHandlerContext';
import useCheckoutRedirect from './useCheckoutRedirect';

interface StartCheckoutArgs extends ProductSelection {
  successPath?: string;
  cancelPath?: string;
  productLookupKey?: string;
  entryPoint?: string;
}

/**
 * The one way a product card starts a checkout.
 *
 * Every card must go through this. Four of them used to own a `useMutation` with an inlined
 * `window.location.href`, which silently opted them out of the Speedy Flow — clicking them tore
 * the modal down and navigated the page to Stripe, the exact thing the flow exists to prevent.
 *
 * Three behaviours, in order:
 * 1. Inside the flow with no account yet, the selection is recorded and no request is made — there
 *    is nobody to bill until sign-up finishes.
 * 2. Inside the flow with an account, a tab is reserved *synchronously* (see below), the session is
 *    created, and the URL is handed to the flow to load into that tab.
 * 3. Outside the flow, the page navigates to Stripe as it always did.
 *
 * The tab is reserved during the click rather than after the request returns because browsers only
 * allow `window.open` while a user gesture is being handled. Opening it after an await is treated
 * as an unsolicited popup and blocked, which would silently demote every purchase to the same-tab
 * fallback.
 */
const useProductCheckout = () => {
  const interception = useCheckoutInterception();
  const redirectToCheckout = useCheckoutRedirect();

  const { mutate, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    // `variables` carries the selection back out. The checkout URL says nothing about what is in
    // it, so this is the only way the flow can name the purchase once it completes.
    onSuccess: (data, variables) => redirectToCheckout(data.url, variables),
    // Inside the flow a tab was reserved during the click, before this request was made. Only the
    // flow can release it and decide what the modal shows next, so the refusal goes back to it.
    // Outside the flow there is nothing reserved and nothing waiting — the card simply stops
    // pending, as it always did.
    onError: (error) => interception?.onCheckoutFailed?.(error),
  });

  const start = useCallback(
    ({
      productId,
      priceId,
      associatedHabitatIds,
      offerId,
      successPath,
      cancelPath,
      productLookupKey,
      entryPoint,
    }: StartCheckoutArgs) => {
      trackMP('request-checkout', {
        productId,
        priceId,
        productLookupKey,
        entryPoint,
        // The habitat a generic product is being bought for, when there is one.
        habitatId: associatedHabitatIds?.[0],
      });

      if (interception?.onSelectProduct) {
        interception.onSelectProduct({ productId, priceId, associatedHabitatIds, offerId });
        return;
      }

      interception?.reserveCheckoutTab?.();
      // Inside the flow the return paths carry a marker, so the tab reports back instead of
      // navigating. Outside it they are used as-is.
      mutate({
        productId,
        priceId,
        associatedHabitatIds,
        offerId,
        successPath: interception?.markReturnPath?.(successPath) ?? successPath,
        cancelPath: interception?.markReturnPath?.(cancelPath) ?? cancelPath,
      });
    },
    [interception, mutate],
  );

  return { start, isPending };
};

export default useProductCheckout;
